import { useState, useEffect } from 'react';
import { Plus as AddIcon, Pencil as EditIcon, Trash2 as DeleteIcon } from 'lucide-react';
import { Button, FormField, Input } from '@coordio/ui';
import { EmptyState, FormSection, InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { agentService } from '../../services/agentService';
import { useNotificationStore } from '../../store/notificationStore';
import { useConfirmDialog } from '../common/ConfirmDialog';
import { CodeEditor } from '../common/CodeEditor';
import type { AgentMarkdownFileDto } from '../../types/agent';

interface AgentMarkdownFileListProps {
  agentId: string;
  readOnly?: boolean;
}

/**
 * List and CRUD for agent markdown files (File Search knowledge files).
 * Uses CortexDialog + FormField + design tokens for coordio-ui foundation.
 */
export const AgentMarkdownFileList = ({ agentId, readOnly = false }: AgentMarkdownFileListProps) => {
  const confirm = useConfirmDialog();
  const [files, setFiles] = useState<AgentMarkdownFileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFile, setEditingFile] = useState<AgentMarkdownFileDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formSortOrder, setFormSortOrder] = useState(0);
  const { addNotification } = useNotificationStore();

  const loadFiles = async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getMarkdownFiles(agentId);
      setFiles(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load markdown files';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [agentId]);

  const handleAdd = () => {
    setEditingFile(null);
    setFormName('');
    setFormContent('');
    setFormSortOrder(files.length);
    setOpenDialog(true);
  };

  const handleEdit = (file: AgentMarkdownFileDto) => {
    setEditingFile(file);
    setFormName(file.name);
    setFormContent(file.content);
    setFormSortOrder(file.sortOrder);
    setOpenDialog(true);
  };

  const handleDelete = async (file: AgentMarkdownFileDto) => {
    const ok = await confirm({
      title: 'Delete file',
      message: `Delete "${file.name}"?`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await agentService.deleteMarkdownFile(agentId, file.id);
      addNotification('File deleted', 'success');
      loadFiles();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete file', 'error');
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingFile(null);
  };

  const handleDialogSave = async () => {
    const name = formName.trim();
    if (!name) {
      addNotification('Name is required', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingFile) {
        await agentService.updateMarkdownFile(agentId, editingFile.id, {
          name,
          content: formContent,
          sortOrder: formSortOrder,
        });
        addNotification('File updated', 'success');
      } else {
        await agentService.createMarkdownFile(agentId, {
          name,
          content: formContent,
          sortOrder: formSortOrder,
        });
        addNotification('File added', 'success');
      }
      handleDialogClose();
      loadFiles();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to save file', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FormSection title="Markdown files (File Search)" data-testid="agent-section-markdown-files">
        <LoadingSkeleton rows={3} />
      </FormSection>
    );
  }

  return (
    <FormSection
      title="Markdown files (File Search)"
      description="Knowledge files the agent can search during conversations via the File Search tool."
      data-testid="agent-section-markdown-files"
    >
      {!readOnly && (
        <div className="flex justify-end mb-3">
          <Button type="button" variant="outline" size="sm" onClick={handleAdd} data-testid="add-markdown-file-btn">
            <AddIcon size={16} className="mr-1" />
            Add file
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-3">
          <InlineErrorPanel
            message={error}
            action={
              <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            }
          />
        </div>
      )}

      {files.length === 0 ? (
        <EmptyState
          title="No markdown files"
          description={
            readOnly
              ? 'This agent has no markdown files.'
              : 'Add files to enable the agent to search this knowledge during conversations.'
          }
        />
      ) : (
        <div className="border border-[var(--color-border-default)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-default)] bg-[var(--color-bg-subtle)]">
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Name</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-secondary)]">Order</th>
                {!readOnly && (
                  <th className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-secondary)] w-[120px]">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.id} className="border-b border-[var(--color-border-subtle)] last:border-b-0 hover:bg-[var(--color-bg-subtle)]">
                  <td className="px-3 py-2 text-[var(--color-text-primary)]">{file.name}</td>
                  <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{file.sortOrder}</td>
                  {!readOnly && (
                    <td className="px-3 py-2 text-right">
                      <Button type="button" variant="ghost" size="icon" onClick={() => handleEdit(file)} aria-label="Edit file">
                        <EditIcon size={16} />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" className="text-[var(--color-danger)]" onClick={() => handleDelete(file)} aria-label="Delete file">
                        <DeleteIcon size={16} />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Add / Edit dialog ──────────────────────────────────────────── */}
      <CortexDialog
        open={openDialog}
        onClose={handleDialogClose}
        title={editingFile ? 'Edit markdown file' : 'Add markdown file'}
        size="lg"
        data-testid="markdown-file-dialog"
      >
        <CortexDialogBody>
          <div className="flex flex-col gap-3">
            <FormField label="File name" required>
              <Input
                autoFocus
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </FormField>

            <FormField label="Sort order">
              <Input
                type="number"
                value={formSortOrder}
                onChange={(e) => setFormSortOrder(parseInt(e.target.value, 10) || 0)}
                min={0}
              />
            </FormField>

            <FormField label="Content (Markdown)">
              <CodeEditor
                value={formContent}
                onChange={setFormContent}
                language="markdown"
                height="320px"
                readOnly={false}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button type="button" variant="outline" onClick={handleDialogClose}>
            Cancel
          </Button>
          <Button type="button" variant="default" onClick={handleDialogSave} disabled={saving}>
            {saving ? 'Saving…' : editingFile ? 'Update' : 'Add'}
          </Button>
        </CortexDialogFooter>
      </CortexDialog>
    </FormSection>
  );
};
